// ***************************************************************************************
// Collapse, ported from game/blockgrid.cpp.
//
// This is the rule most likely to be got wrong by assumption, so read it rather than
// guessing:
//
//   moveDown   (BlockGrid_MoveBlocksDown,  blockgrid.cpp:1374)
//              PER COLUMN. For each column, walk y up from the floor; every gap pulls
//              down the nearest tile above it in that same column.
//
//   moveRight  (BlockGrid_MoveBlocksRight, blockgrid.cpp:1090)
//              PER ROW. For each row, walk x in from the right edge; every gap pulls in
//              the nearest tile to its left IN THAT SAME ROW.
//
// Rightward compaction therefore moves INDIVIDUAL TILES WITHIN A ROW. Whole columns do
// not slide as units - a row closes its own holes independently of every other row.
//
// BlockGrid_SettleStep runs them in that order and repeats while anything moves, so
// gravity is retried after every sideways pass and the resting state is everything packed
// into the bottom-right corner. lastXDirection is initialised to RIGHT, so the sideways
// slide happens from the first word of every game rather than being an edge case.
//
// y = 0 is the FLOOR. Index is [y * w + x].
// ***************************************************************************************

/** A board cell: a lowercase letter, or null for an empty cell. */
export type Cell = string | null;
export type Board = Cell[];

export function moveDown(board: Board, w: number, h: number): boolean {
  let moved = false;

  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h - 1; y++) {
      if (board[y * w + x] != null) continue;

      for (let look = y + 1; look < h; look++) {
        if (board[look * w + x] == null) continue;

        board[y * w + x] = board[look * w + x]!;
        board[look * w + x] = null;
        moved = true;
        break;
      }
    }
  }

  return moved;
}

export function moveRight(board: Board, w: number, h: number): boolean {
  let moved = false;

  for (let y = 0; y < h; y++) {
    for (let x = w - 1; x >= 1; x--) {
      if (board[y * w + x] != null) continue;

      for (let look = x - 1; look >= 0; look--) {
        if (board[y * w + look] == null) continue;

        board[y * w + x] = board[y * w + look]!;
        board[y * w + look] = null;
        moved = true;
        break;
      }
    }
  }

  return moved;
}

/**
 * Settle the board in place. Returns false only if it failed to converge.
 *
 * Bounded rather than `while (true)`: this also runs on move logs arriving from the
 * network, and a settle that cannot converge must fail rather than spin. Each pass moves
 * at least one tile and there are w*h tiles, so anything past that bound is a bug in the
 * rules above, not a slow case.
 */
export function settle(board: Board, w: number, h: number): boolean {
  for (let pass = 0; pass < w * h + 2; pass++) {
    if (moveDown(board, w, h)) continue;
    if (moveRight(board, w, h)) continue;

    return true;
  }

  return false;
}

/**
 * The settle as a sequence of intermediate boards, for animation.
 *
 * The LOGICAL result is whatever `settle` says it is - this exists so the renderer can
 * show the stages, never so it can decide them. The caller applies `settle` to get the
 * final state and uses these frames purely to interpolate towards it.
 *
 * Each stage says WHICH PASS produced it, because a drop and a slide are different
 * movements and should not look the same: tiles fall under gravity and accelerate, then
 * pack sideways and ease to a stop. Without the label the renderer can only animate both
 * identically, which is what made the collapse read as a single diagonal shuffle rather
 * than the two rules it actually is.
 */
export type SettleStageKind = 'down' | 'right';

export interface SettleStage {
  readonly kind: SettleStageKind;
  readonly board: Board;
}

export function settleStages(board: Board, w: number, h: number): SettleStage[] {
  const stages: SettleStage[] = [];
  const working = board.slice();

  for (let pass = 0; pass < w * h + 2; pass++) {
    if (moveDown(working, w, h)) {
      stages.push({ kind: 'down', board: working.slice() });
      continue;
    }

    if (moveRight(working, w, h)) {
      stages.push({ kind: 'right', board: working.slice() });
      continue;
    }

    break;
  }

  return stages;
}
